/**
 * Transform dialog (W2-11 batch-tools; DESIGN.md §5.4 "Transform...", U§5 B5): exact time offset, time scale p/q
 * with a pivot, X offset and X scale with a pivot, over the selected notes and keys together, through
 * `Core.transform.transform`. The result shows live behind the dialog (gesture) and Apply records one transaction.
 */

import { useMemo, useState } from 'react';
import type { DialogProps } from '../../app/registry/types';
import { Core } from '../../core/commands/index';
import type { TransformSpec } from '../../core/commands/types';
import type { TxBuilder } from '../../core/ops/types';
import { Beats } from '../../core/time/beat';
import { formatBeat } from '../../core/time/beatSyntax';
import type { Beat } from '../../core/time/types';
import type { KeyId, NoteId } from '../../core/model/types';
import type { DocStore } from '../../state/types';
import { Button } from '../../ui/Button';
import { Dialog } from '../../ui/Dialog';
import { Segmented } from '../../ui/Segmented';
import { Select } from '../../ui/Select';
import { m } from './batch.messages';
import s from './Batch.module.css';
import { playheadBeat, ref, toastNotes } from './batchContext';
import {
  BeatInput,
  NumberInput,
  StatusLine,
  useApplyOnEnter,
  useBeatContext,
  useText,
  type SheetStatus,
} from './sheetKit';
import { useToolGesture } from './useToolGesture';

/** Time scale presets p/q (p/q > 1 stretches, < 1 squeezes). */
export const SCALE_PRESETS: readonly (readonly [number, number])[] = [
  [1, 1],
  [2, 1],
  [3, 2],
  [4, 3],
  [1, 2],
  [2, 3],
  [3, 4],
];

type TimePivot = 'start' | 'end' | 'playhead';

interface Extent {
  readonly lo: Beat | null;
  readonly hi: Beat | null;
  readonly xCenter: number;
}

/** Time span of the items and the X centre of the notes. */
export function selectionExtent(store: DocStore | null, notes: readonly NoteId[], keys: readonly KeyId[]): Extent {
  const span: { lo: Beat | null; hi: Beat | null } = { lo: null, hi: null };
  let xMin = Infinity;
  let xMax = -Infinity;
  const take = (b: Beat): void => {
    if (span.lo === null || Beats.lt(b, span.lo)) span.lo = b;
    if (span.hi === null || Beats.lt(span.hi, b)) span.hi = b;
  };
  if (store !== null) {
    for (const id of notes) {
      const n = store.doc.notes.get(id);
      if (n === undefined) continue;
      take(n.t);
      take(n.end);
      xMin = Math.min(xMin, n.x);
      xMax = Math.max(xMax, n.x);
    }
    for (const id of keys) {
      const k = store.doc.keys.get(id);
      if (k !== undefined) take(k.t);
    }
  }
  return { lo: span.lo, hi: span.hi, xCenter: Number.isFinite(xMin) ? (xMin + xMax) / 2 : 0 };
}
type XPivot = 'zero' | 'center';

export function TransformDialog(p: DialogProps<'transform'>) {
  const { services, props } = p;
  const text = useText();
  const store = services.store;
  const beatCtx = useBeatContext(services);
  // Selection extent and X centre, read before the preview moves anything.
  const [extent] = useState(() => selectionExtent(store, props.notes, props.keys));
  const [playhead] = useState(() => props.pivotBeat ?? playheadBeat(services));
  const [dt, setDt] = useState<Beat>(Beats.ZERO);
  const [scaleKey, setScaleKey] = useState('1/1');
  const [pq, setPq] = useState<readonly [number, number]>([1, 1]);
  const [timePivot, setTimePivot] = useState<TimePivot>('start');
  const [dx, setDx] = useState(0);
  const [xScale, setXScale] = useState(1);
  const [xPivot, setXPivot] = useState<XPivot>('zero');

  const count = props.notes.length + props.keys.length;
  const pivotBeat: Beat | null = timePivot === 'playhead' ? playhead : timePivot === 'end' ? extent.hi : extent.lo;
  const spec = useMemo<TransformSpec>(
    () => ({
      dt,
      timeScale: pq,
      ...(pivotBeat === null ? {} : { timePivot: pivotBeat }),
      dx,
      xScale,
      xPivot: xPivot === 'zero' ? 0 : extent.xCenter,
    }),
    [dt, pq, pivotBeat, dx, xScale, xPivot, extent],
  );
  const identity = Beats.eq(dt, Beats.ZERO) && pq[0] === pq[1] && dx === 0 && xScale === 1;
  const build = useMemo(
    () =>
      count === 0 || identity
        ? null
        : (tx: TxBuilder): null => {
            Core.transform.transform(tx, { notes: props.notes, keys: props.keys, spec });
            return null;
          },
    [count, identity, props.notes, props.keys, spec],
  );
  const gesture = useToolGesture<null>({ services, label: ref(m.txnTransform), command: 'transform.open', build });

  const outcome = gesture.outcome;
  const status: SheetStatus =
    count === 0
      ? { kind: 'error', text: text.t(m.transformNothing) }
      : gesture.status === 'paused'
        ? { kind: 'warn', text: text.t(m.previewPaused) }
        : outcome !== null && !outcome.ok
          ? { kind: 'error', text: text.tr(outcome.reason) }
          : identity
            ? { kind: 'none' }
            : { kind: 'ok', text: text.t(m.transformStatus, { count }) };

  const apply = (): void => {
    if (identity) {
      gesture.cancel();
      p.close(null);
      return;
    }
    const out = gesture.apply();
    if (out === null || !out.ok) return;
    services.ui.toast(ref(m.transformDone, { count }), { kind: 'success', undo: out.txn !== null });
    toastNotes(services, out.notes);
    p.close({ applied: true });
  };
  const cancel = (): void => {
    gesture.cancel();
    p.close(null);
  };
  const enter = useApplyOnEnter(apply, count === 0);

  const scaleOptions = [
    ...SCALE_PRESETS.map(([a, b]) => ({
      value: `${String(a)}/${String(b)}`,
      label: text.t(a === b ? m.scaleNone : a > b ? m.scaleStretch : m.scaleSqueeze, { p: a, q: b }),
    })),
    { value: 'custom', label: text.t(m.scaleCustom) },
  ];

  return (
    <Dialog
      open
      size="md"
      title={text.t(m.transformTitle)}
      description={text.t(m.transformDescription, { count })}
      onClose={cancel}
      footer={
        <>
          <div className={s.footerStatus}>
            <StatusLine status={status} />
          </div>
          <Button variant="ghost" onClick={cancel}>
            {text.t(m.cancel)}
          </Button>
          <Button variant="primary" onClick={apply} disabledReason={count === 0 ? ref(m.transformNothing) : null}>
            {text.t(m.apply)}
          </Button>
        </>
      }
    >
      <div className={s.dialogBody} {...enter}>
        <div className={s.group}>
          <p className={s.groupTitle}>{text.t(m.transformTime)}</p>
          <BeatInput label={text.t(m.timeOffset)} value={dt} ctx={beatCtx} onChange={setDt} />
          <div className={s.row}>
            <Select<string>
              label={text.t(m.timeScale)}
              value={scaleKey}
              options={scaleOptions}
              onChange={(v) => {
                setScaleKey(v);
                if (v !== 'custom') {
                  const [a, b] = v.split('/').map(Number);
                  setPq([a ?? 1, b ?? 1]);
                }
              }}
            />
            {scaleKey === 'custom' && (
              <>
                <NumberInput
                  label={text.t(m.scaleP)}
                  value={pq[0]}
                  integer
                  min={1}
                  max={1000}
                  onChange={(v) => {
                    setPq([v, pq[1]]);
                  }}
                />
                <NumberInput
                  label={text.t(m.scaleQ)}
                  value={pq[1]}
                  integer
                  min={1}
                  max={1000}
                  onChange={(v) => {
                    setPq([pq[0], v]);
                  }}
                />
              </>
            )}
          </div>
          <Segmented<TimePivot>
            label={text.t(m.timePivot)}
            value={timePivot}
            options={[
              {
                value: 'start',
                label: text.t(m.pivotStart, { beat: extent.lo === null ? '-' : formatBeat(extent.lo) }),
              },
              { value: 'end', label: text.t(m.pivotEnd, { beat: extent.hi === null ? '-' : formatBeat(extent.hi) }) },
              { value: 'playhead', label: text.t(m.pivotPlayhead, { beat: formatBeat(playhead) }) },
            ]}
            onChange={setTimePivot}
          />
        </div>
        <div className={s.group}>
          <p className={s.groupTitle}>{text.t(m.transformX)}</p>
          <div className={s.row}>
            <NumberInput label={text.t(m.xOffset)} value={dx} unit={text.t(m.unitPx)} onChange={setDx} />
            <NumberInput label={text.t(m.xScale)} value={xScale} step={0.1} onChange={setXScale} />
          </div>
          <Segmented<XPivot>
            label={text.t(m.xPivot)}
            value={xPivot}
            options={[
              { value: 'zero', label: text.t(m.xPivotZero) },
              { value: 'center', label: text.t(m.xPivotCenter, { x: Number(extent.xCenter.toPrecision(6)) }) },
            ]}
            onChange={setXPivot}
          />
          <p className={s.hint}>{text.t(m.transformXHint)}</p>
        </div>
      </div>
    </Dialog>
  );
}
